/**
 * Validation utilities for form inputs
 */

/**
 * Validates Dominican Republic ID (Cédula)
 * Must be exactly 11 digits and follow Dominican ID format
 */
export const validateDominicanID = (cedula: string): boolean => {
  // Remove any non-numeric characters
  const cleanCedula = cedula.replace(/\D/g, '');
  
  // Must be exactly 11 digits
  if (cleanCedula.length !== 11) {
    return false;
  }
  
  // Dominican ID validation algorithm
  const weights = [1, 2, 1, 2, 1, 2, 1, 2, 1, 2];
  let sum = 0;
  
  for (let i = 0; i < 10; i++) {
    const multiplicacion = parseInt(cleanCedula[i]) * weights[i];
    
    // If the result is greater than 9, subtract 9 (alternative algorithm)
    sum += multiplicacion < 10 ? multiplicacion : multiplicacion - 9;
  }
  
  // Calculate check digit
  const checkDigit = (10 - (sum % 10)) % 10;
  
  return checkDigit === parseInt(cleanCedula[10]);
};

/**
 * Calculates age from date of birth
 */
export const calculateAge = (dateOfBirth: string): number => {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};

/**
 * Validates that a person is at least 18 years old
 */
export const validateMinimumAge = (dateOfBirth: string, minimumAge: number = 18): boolean => {
  const age = calculateAge(dateOfBirth);
  return age >= minimumAge;
};

/**
 * Formats Dominican ID for display (adds dashes)
 */
export const formatDominicanID = (cedula: string): string => {
  const cleanCedula = cedula.replace(/\D/g, '');
  if (cleanCedula.length === 11) {
    return `${cleanCedula.slice(0, 3)}-${cleanCedula.slice(3, 10)}-${cleanCedula.slice(10)}`;
  }
  return cedula;
};

/**
 * Removes formatting from Dominican ID
 */
export const cleanDominicanID = (cedula: string): string => {
  return cedula.replace(/\D/g, '');
};

/**
 * Applies mask formatting to Dominican ID as user types
 * Format: 000-0000000-0
 */
export const applyDominicanIDMask = (value: string): string => {
  // Remove all non-numeric characters
  const numbers = value.replace(/\D/g, '');
  
  // Limit to 11 digits
  const limitedNumbers = numbers.slice(0, 11);
  
  // Apply mask formatting based on length
  if (limitedNumbers.length === 0) {
    return '';
  } else if (limitedNumbers.length <= 3) {
    return limitedNumbers;
  } else if (limitedNumbers.length <= 10) {
    return `${limitedNumbers.slice(0, 3)}-${limitedNumbers.slice(3)}`;
  } else {
    // 11 digits - complete format
    return `${limitedNumbers.slice(0, 3)}-${limitedNumbers.slice(3, 10)}-${limitedNumbers.slice(10)}`;
  }
};

/**
 * Handles input change for Dominican ID with mask
 */
export const handleDominicanIDInput = (
  value: string,
  setValue: (value: string) => void
): void => {
  const maskedValue = applyDominicanIDMask(value);
  setValue(maskedValue);
};

/**
 * Gets the maximum date allowed for date of birth (18 years ago from today)
 */
export const getMaxDateOfBirth = (): string => {
  const today = new Date();
  const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  return maxDate.toISOString().split('T')[0];
};

/**
 * Gets the minimum date allowed for date of birth (reasonable limit, e.g., 120 years ago)
 */
export const getMinDateOfBirth = (): string => {
  const today = new Date();
  const minDate = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate());
  return minDate.toISOString().split('T')[0];
};
